import React from "react";
import { Card, CardContent, Typography, Box, Divider, Grid } from "@mui/material";
import { FaRegCheckCircle, FaRegTimesCircle, FaEllipsisH } from "react-icons/fa";
import { formatDateTimeBr } from "shared/util/formatters/dateTimeBr";
import { Item, ItemListProps } from "../types/types";

const formatDate = (dateString: string) => formatDateTimeBr(dateString);

const renderStatusIcon = (status?: string) => {
  if (status === "error" || status === "recusado") {
    return <FaRegTimesCircle color="red" size={20} />;
  }
  if (status === "pending" || status === "pendente") {
    return <FaEllipsisH color="#9e9e9e" size={20} />;
  }
  return <FaRegCheckCircle color="#4caf50" size={20} />;
};

const TimelineItemList: React.FC<ItemListProps> = ({ items }) => {
  if (!items.length) return null;

  const hasMultipleItems = items.length > 1;

  const renderLinePoints = (isFirstItem: boolean, isLastItem: boolean) => {
    if (!hasMultipleItems) return null;

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          height: "100%",
        }}
      >
        {isFirstItem ? (
          <Divider
            sx={{
              flex: 1,
              width: 0,
              backgroundColor: "#757575",
              marginBottom: 1,
            }}
          />
        ) : (
          <Divider
            orientation="vertical"
            sx={{
              flex: 1,
              width: 2,
              backgroundColor: "#757575",
              marginBottom: 1,
            }}
          />
        )}
        <Box
          sx={{
            width: 15,
            height: 15,
            borderRadius: "50%",
            backgroundColor: "#4caf50",
            margin: "8px 0",
          }}
        />
        {isLastItem ? (
          <Divider
            sx={{
              flex: 1,
              width: 0,
              backgroundColor: "#757575",
              marginTop: 1,
            }}
          />
        ) : (
          <Divider
            orientation="vertical"
            sx={{
              flex: 1,
              width: 2,
              backgroundColor: "#757575",
              marginTop: 1,
            }}
          />
        )}
      </Box>
    );
  };

  const renderCard = (item: Item) => {
    return (
      <Card
        sx={{
          marginBottom: 2,
          borderRadius: 2,
          boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
        }}
      >
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {renderStatusIcon(item.status)}
            <Typography variant="body1" sx={{ fontWeight: "bold" }}>
              {item.nome}
            </Typography>
          </Box>
          <Typography variant="body2" color="textSecondary" sx={{ marginLeft: 4 }}>
            {item.data ? formatDate(item.data) : "Data não disponível"}
          </Typography>
          {item.observacoes && <Divider sx={{ marginY: 1 }} />}
          {item.observacoes && (
            <Typography variant="body2" color="textSecondary" sx={{ marginLeft: 4 }}>
              {item.observacoes}
            </Typography>
          )}
          <Divider sx={{ marginY: 1 }} />
          <Typography variant="body2" color="textSecondary" sx={{ marginLeft: 4, marginTop: 1 }}>
            {item.email}
          </Typography>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box>
      {items.map((item, index) => {
        const isFirstItem = index === 0;
        const isLastItem = index === items.length - 1;

        return (
          <React.Fragment key={item.id}>
            <Grid container spacing={3} sx={{ display: "flex", flexDirection: "row" }}>
              {hasMultipleItems && (
                <Grid item xs={2}>
                  {renderLinePoints(isFirstItem, isLastItem)}
                </Grid>
              )}
              <Grid item xs={hasMultipleItems ? 10 : 12}>
                {renderCard(item)}
              </Grid>
            </Grid>
          </React.Fragment>
        );
      })}
    </Box>
  );
};

export default TimelineItemList;
